import {
  InfiniteQueryObserverOptions,
  QueryKey,
  useInfiniteQuery,
} from 'react-query';

export type InfiniteQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Pick<
  InfiniteQueryObserverOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryFnData,
    TQueryKey
  >,
  | 'cacheTime'
  | 'enabled'
  | 'getNextPageParam'
  | 'getPreviousPageParam'
  | 'initialData'
  | 'keepPreviousData'
  | 'meta'
  | 'onError'
  | 'onSettled'
  | 'onSuccess'
  | 'placeholderData'
  | 'refetchInterval'
  | 'refetchOnWindowFocus'
  | 'retry'
  | 'staleTime'
>;

export const useCreateInfiniteQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  key: readonly unknown[],
  fetch: (params?: object) => Promise<TQueryFnData>,
  params?: object,
  options?: InfiniteQueryOptions<TQueryFnData, TError, TData>,
) => {
  const query = useInfiniteQuery<TQueryFnData, TError, TData>(
    [...key],
    ({ pageParam = 1 }) => fetch({ page: pageParam, ...params }),
    {
      getNextPageParam: (_, allPages) => {
        // @ts-ignore FIXME `.length` is not applying to the array but to TQueryFnData
        if (allPages[allPages.length - 1].length === 0) {
          return;
        }
        return allPages.length + 1;
      },
      ...options,
    },
  );

  return {
    data: query.data,
    fetchNextPage: query.fetchNextPage,
    fetchPreviousPage: query.fetchPreviousPage,
    hasNextPage: query.hasNextPage,
    hasPreviousPage: query.hasPreviousPage,
    isError: query.isError,
    isSuccess: query.isSuccess,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchingPreviousPage: query.isFetchingPreviousPage,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
